import { useCallback, useMemo, useSyncExternalStore } from "react";

import { useGetUserDetails } from "@/hooks/useGetUserDetails";

/**
 * Profile warnings a member has minimized to a one-line strip, per firm and
 * user, per browser. A view setting only: dismissing for the firm is on the
 * server. Each id names the warning and the facts it is about, so a warning
 * whose facts change opens again.
 *
 * Warnings this browser dismissed before dismissal moved to the server
 * (`caseblink.dismissedWarnings.v1`) read as minimized for that user; they
 * are never dismissed for the firm on their behalf.
 */
const storageKey = (firmId: string, uid: string) =>
  `caseblink.minimizedWarnings.v1.${firmId || "-"}.${uid || "-"}`;
export const legacyDismissedKey = (firmId: string, uid: string) =>
  `caseblink.dismissedWarnings.v1.${firmId || "-"}.${uid || "-"}`;

const EVENT = "caseblink:minimized-warnings";

const read = (key: string): string[] => {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
};

const write = (key: string, ids: string[]): void => {
  try {
    if (ids.length) localStorage.setItem(key, JSON.stringify(ids));
    else localStorage.removeItem(key);
  } catch {
    // Private mode: minimized for this page only.
  }
  window.dispatchEvent(new Event(EVENT));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
};

const snapshotOf = (key: string) => () => {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
};

export const useMinimizedWarnings = (firmId: string | null | undefined) => {
  const uid = useGetUserDetails().userDetails.id ?? "";
  const firm = (firmId ?? "").trim();
  const key = storageKey(firm, uid);
  const legacy = legacyDismissedKey(firm, uid);
  const raw = useSyncExternalStore(
    subscribe,
    useCallback(() => snapshotOf(key)(), [key])
  );
  const rawLegacy = useSyncExternalStore(
    subscribe,
    useCallback(() => snapshotOf(legacy)(), [legacy])
  );
  const minimized = useMemo(() => new Set(raw ? read(key) : []), [raw, key]);
  const legacyIds = useMemo(
    () => new Set(rawLegacy ? read(legacy) : []),
    [rawLegacy, legacy]
  );
  return {
    /** `legacyId`: the warning's id in the old dismissed list, if it had one. */
    isMinimized: useCallback(
      (id: string, legacyId?: string) =>
        minimized.has(id) || Boolean(legacyId && legacyIds.has(legacyId)),
      [minimized, legacyIds]
    ),
    minimize: useCallback(
      (id: string) => write(key, Array.from(new Set([...read(key), id]))),
      [key]
    ),
    expand: useCallback(
      (id: string, legacyId?: string) => {
        write(
          key,
          read(key).filter((x) => x !== id)
        );
        if (legacyId)
          write(
            legacy,
            read(legacy).filter((x) => x !== legacyId)
          );
      },
      [key, legacy]
    )
  };
};
